/**
 *
 * When the link is clicked on it opens a dialog element to the video embed.
 * This is much simpler than using something like colorbox.
 *
 */
import onDocumentLoaded from "../../lib/onDocumentLoaded";

type VideoModalsArgs = {
	customPlayButton?: string|false
};
export function videoModals(config: VideoModalsArgs) {
	onDocumentLoaded(() => {
		const VIDEO_DOMAINS = ["vimeo.com", "youtube.com", "youtu.be"];
		const anchorQuery = `a:where(${VIDEO_DOMAINS.map(domain => `[href*="${domain}"]`).join(", ")})`;

		// On load, we wire up existing anchors with media links
		for (const element of document.body.querySelectorAll<HTMLAnchorElement>(anchorQuery)) {
			wireUpAnchorElement(element, config?.customPlayButton);
		}

		// This makes sure that AJAX doesn't break the popups
		new MutationObserver(mutations => {
			for (const mutation of mutations) {
				for (const addedNode of mutation.addedNodes) {
					if (addedNode instanceof HTMLAnchorElement) { // If an anchor is added, we can just check it directly
						if (addedNode.href && VIDEO_DOMAINS.find(domain => addedNode.href.includes(domain))) {
							wireUpAnchorElement(addedNode, config?.customPlayButton);
						}
					} else if (addedNode instanceof Element) { // If any other element is added, we need to query within it
						for (const element of addedNode.querySelectorAll<HTMLAnchorElement>(anchorQuery)) {
							wireUpAnchorElement(element, config?.customPlayButton);
						}
					}
				}
			}
		}).observe(document.body, {childList: true, subtree: true});
	});
}

function addCustomPlayButton(element: HTMLAnchorElement, customPlayButton: string) {
	const span = document.createElement('span');
	span.classList.add('custom-play-button');
	span.innerHTML = customPlayButton;
	element.classList.add('has-custom-play');
	element.appendChild(span);
}

function wireUpAnchorElement(element: HTMLAnchorElement, customPlayButton?: unknown) {
	if (element.classList.contains("attached-video-modal")) {
		return;
	}
	element.classList.add("attached-video-modal");
	element.setAttribute('aria-label', 'Video modal')

	//* DEBUG */console.log(element);
	if (element.querySelector('img')) {
		element.classList.add('has-play-button');
		if (typeof customPlayButton === 'string') {
			element.classList.add('has-play-button--custom');
			addCustomPlayButton(element, customPlayButton);
		} else {
			element.classList.add('has-play-button--basic');
		}
	}
	element.addEventListener('click', event => {
		if (openVideoDialog(element)) {
			event.preventDefault();
		}
	});
}

function openVideoDialog(element: HTMLAnchorElement) {
	//* DEBUG */console.log('openVideoDialog', href);
	const href = element.href;
	// Create the dialog element using a template literal
	const dialog = document.createElement('dialog');
	dialog.classList.add('dialog-video');
	dialog.innerHTML = `
        <form method="dialog">
            <button autofocus><span class="screen-reader-text">Close</span> <i class="icon"></i></button>
        </form>
    `;

	const iframe = document.createElement('iframe');
	iframe.allow = 'autoplay';
	iframe.width = '560';
	iframe.height = '315';

	if (href.includes('vimeo.com') && !href.includes('?s=')) {
		iframe.src = href.replace('https://vimeo.com/', 'https://player.vimeo.com/video/');
		iframe.width = '500';
		iframe.height = '281';
	} else if (href.includes('youtube.com/watch?v=') && !href.includes('?s=')) {
		iframe.src = 'https://www.youtube.com/embed/' + href.split('v=')[1];
	} else if (href.includes('youtu.be/') && !href.includes('?s=')) {
		iframe.src = 'https://www.youtube.com/embed/' + href.split('youtu.be/')[1];
	} else {
		console.error(`Attempted to open a dialog for an unsupported URL: ${href}`);
		return false;
	}

	dialog.appendChild(iframe);
	dialog.addEventListener("close", () => {
		dialog.remove();
		element.focus()
	});

	// Append the dialog to the body and show it
	document.body.appendChild(dialog);
	dialog.showModal();
	return true;
}
